import { capitalizeString } from '@strata/helper-functions';
import { FilterPane } from '@strata/react-filter-pane';
import { FormSection, FormSectionWithMessage } from '@strata/react-form-section';
import { type ColumnDef, Grid, type KpiData, KpiSection, PrimaryData } from '@strata/react-grid';
import { formatRateString } from '@strata/react-servicing-rate';

import { ProductStatsColumns } from 'components/products/ProductStatsColumns';
import { TimeFrameBanner } from 'components/shared/TimeframeBanner';
import { ViewsControls } from 'components/shared/ViewsControls';
import { type MedicationDetails, type MedicationStats } from 'store/api/apertureApi';
import { useGetPlaceOfServiceMapQuery } from 'store/api/referenceApi';

const Sections = {
    PLACE_OF_SERVICE: 'place_of_service',
    LINE_OF_BUSINESS: 'line_of_business',
    BENEFIT: 'benefit',
} as const;
type SectionMap = Record<
    (typeof Sections)[keyof typeof Sections],
    {
        title: string;
        defaultValues?: { value: string }[];
        formatValue: (str?: string) => string | undefined;
    }
>;

const initialSortProperties = [{ id: 'value', desc: false }];

const createDefaultValues = (values?: string[]) => values?.map((value) => ({ value }));

const columns = (
    sectionTitle: string,
    sectionMap: SectionMap,
    key: (typeof Sections)[keyof typeof Sections],
): ColumnDef<MedicationStats>[] => [
    {
        accessorKey: 'value',
        header: sectionTitle,
        size: 120,
        cell: ({ getValue }) => <PrimaryData data={sectionMap[key]?.formatValue(getValue())} />,
    },
    ...ProductStatsColumns,
];

interface ProductDetailsOverviewTabProps {
    productDetails?: MedicationDetails;
    isLoading?: boolean;
}

export const ProductDetailsOverviewTab = ({
    productDetails = undefined,
    isLoading = true,
}: ProductDetailsOverviewTabProps) => {
    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();
    const kpiData: KpiData<MedicationDetails>[] = [
        {
            label: 'Active Referrals',
            value: () => productDetails?.numberOfReferrals,
        },
        {
            label: 'Baseline Rate',
            value: () =>
                formatRateString({
                    bidDecimal: productDetails?.baselineRate,
                    referentPriceType: productDetails?.referentPriceType,
                }),
        },
        {
            label: 'Avg Servicing Rate',
            value: () =>
                formatRateString({
                    bidDecimal: productDetails?.avgWinningBidRate,
                    referentPriceType: productDetails?.referentPriceType,
                }),
        },
    ];

    const sectionMap: SectionMap = {
        [Sections.PLACE_OF_SERVICE]: {
            title: 'Place of Service',
            formatValue: (value) => placeOfServiceMap?.[value ?? ''],
        },
        [Sections.LINE_OF_BUSINESS]: {
            title: 'Line of Business',
            formatValue: (value) => capitalizeString(value ?? 'Not Specified'),
        },
        [Sections.BENEFIT]: {
            title: 'Benefit',
            defaultValues: createDefaultValues(['MEDICAL', 'PHARMACY']),
            formatValue: capitalizeString,
        },
    };

    return (
        <FilterPane
            viewsPaneContent={<ViewsControls />}
            mainContent={
                <>
                    <TimeFrameBanner />
                    <KpiSection className="margin__bottom--medium" kpiData={kpiData} />
                    {Object.values(Sections).map((key) => {
                        const sectionTitle = sectionMap[key]?.title;
                        const values =
                            productDetails?.stats?.find((stat) => stat.key === key)?.values ?? [];
                        return !isLoading && values.length === 0 ? (
                            <FormSectionWithMessage
                                key={key}
                                className="margin__bottom--medium"
                                sectionTitle={sectionTitle}
                                secondaryText={`No data to display for ${sectionTitle}`}
                            />
                        ) : (
                            <FormSection
                                key={key}
                                className="margin__bottom--medium"
                                isLoading={isLoading}
                                sectionTitle={sectionTitle}
                                content={
                                    <Grid
                                        sortProperties={initialSortProperties}
                                        data={
                                            [
                                                ...values,
                                                ...(sectionMap[key]?.defaultValues?.filter(
                                                    ({ value }) =>
                                                        !values.find(
                                                            (item) => value === item.value,
                                                        ),
                                                ) ?? []),
                                            ] as MedicationStats[]
                                        }
                                        columns={columns(sectionTitle, sectionMap, key)}
                                    />
                                }
                            />
                        );
                    })}
                </>
            }
        />
    );
};
